import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useLocationHook from "../../hooks/useLocation";
// import { useCategories } from "../../admin/hooks/useCategories";
// import { useListings } from "../../admin/hooks/useListings";
import { useCategories } from "../../hooks/useCategories";
import { useListings } from "../../hooks/useListings";

const CategoryPostsPage = () => {
  const { categories, loading: catLoading, error: catError } = useCategories();
  const {
    listings,
    loading: listingsLoading,
    error: listingsError,
    fetchListingsByCategory,
  } = useListings();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const categoryId = params.get("category");

  const { getLocation } = useLocationHook();
  const [userLocation, setUserLocation] = useState(null);

  const category = categories.find(
    (cat) => String(cat.id) === String(categoryId)
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);

  // Filtering logic
  const filteredListings = useMemo(() => {
    let filtered = listings;
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (post) =>
          post.post_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (freeOnly) {
      filtered = filtered.filter((post) => {
        // Try to find a price in attributes or description
        if (post.attributes && post.attributes.price !== undefined) {
          return Number(post.attributes.price) === 0;
        }
        // Fallback: look for 'free' in description
        return post.description?.toLowerCase().includes("free");
      });
    }
    if (priceMin !== "" || priceMax !== "") {
      filtered = filtered.filter((post) => {
        let price = null;
        if (post.attributes && post.attributes.price !== undefined) {
          price = Number(post.attributes.price);
        }
        if (price === null || isNaN(price)) return false;
        if (priceMin !== "" && price < Number(priceMin)) return false;
        if (priceMax !== "" && price > Number(priceMax)) return false;
        return true;
      });
    }
    return filtered;
  }, [listings, searchTerm, priceMin, priceMax, freeOnly]);

  useEffect(() => {
    let isMounted = true;
    if (categoryId) {
      getLocation().then((loc) => {
        if (isMounted) {
          setUserLocation(loc);
          console.log("DEBUG: User location:", loc);
          fetchListingsByCategory(categoryId, loc);
        }
      });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [categoryId]);

  if (catLoading) return <div className="p-8 text-center">Loading...</div>;
  if (catError)
    return (
      <div className="p-8 text-center text-red-600">
        Error loading categories.
      </div>
    );
  if (!category)
    return <div className="p-8 text-center">Category not found.</div>;

  return (
    <div className="bg-[#f7f7f7] min-h-screen ">
      {/* Header */}
      <div className="flex items-center justify-between py-4 px-6 bg-white border-b border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline text-xs"
          >
            &lt; back
          </button>
        </div>

        <div className="text-center">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800">
            {category.name}
          </h1>
          {category.description && (
            <p className="text-sm text-gray-500 mt-1">{category.description}</p>
          )}
        </div>

        {/* Spacer to balance the layout */}
        <div className="w-20"></div>
      </div>

      {/* Main content area  */}
      <div className="flex flex-row gap-6 max-w-7xl mx-auto pt-4 px-4 justify-center">
        {/* Sidebar (filters, search, etc.) */}
        <div className="w-48 bg-white rounded-lg shadow p-3 h-fit hidden md:block">
          <h2 className="text-lg font-semibold mb-3">{category.name}</h2>
          <div className="mb-3">
            <input
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder={`search ${category.name.toLowerCase()}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {/* Example filter UI, now functional */}
          <div className="mb-2 flex gap-2">
            <button
              className={`px-2 py-1 rounded text-xs ${
                freeOnly
                  ? "bg-blue-100 text-blue-700"
                  : "bg-gray-100 text-gray-700"
              }`}
              onClick={() => setFreeOnly((v) => !v)}
              type="button"
            >
              Free Only
            </button>
          </div>
          <div className="mb-2">
            <label className="block text-xs text-gray-600 mb-1">
              Price Range
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                className="w-1/2 border border-gray-300 rounded px-1 py-1 text-xs"
                placeholder="$ min"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                min={0}
              />
              <input
                type="number"
                className="w-1/2 border border-gray-300 rounded px-1 py-1 text-xs"
                placeholder="$ max"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                min={0}
              />
            </div>
          </div>
          <div className="text-xs text-gray-500">Avg: $84</div>
        </div>

        {/* Listings grid */}
        <div className="flex-1">
          {listingsLoading ? (
            <div className="text-gray-500 text-center py-8">
              Loading posts...
            </div>
          ) : listingsError ? (
            <div className="text-red-600 text-center py-8">
              Error loading posts.
            </div>
          ) : filteredListings.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No posts yet for this category.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {filteredListings.map((post) => (
                <div
                  key={post.id}
                  className="bg-white rounded-lg shadow p-0 flex flex-col border border-gray-200 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/listings/${post.id}`)}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/listings/${post.id}`);
                  }}
                >
                  {/* Image */}
                  {Array.isArray(post.images) && post.images.length > 0 ? (
                    <img
                      src={post.images[0]}
                      alt="post"
                      className="h-40 w-full object-cover"
                    />
                  ) : (
                    <div className="h-40 w-full bg-gradient-to-r from-blue-100 to-cyan-100 flex items-center justify-center text-xl text-gray-400 font-bold">
                      No Image
                    </div>
                  )}
                  {/* Card content */}
                  <div className="p-3 flex flex-col gap-1">
                    <h2 className="text-base font-semibold text-gray-800 mb-1 truncate">
                      {post.post_title}
                    </h2>
                    <div className="text-xs text-gray-700 mb-2 line-clamp-3">
                      {post.description}
                    </div>
                    {/* Add more post details as needed */}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPostsPage;
